import { createSlice } from '@reduxjs/toolkit';
import { v4 as uuidv4 } from 'uuid';
import axios from '../../utils/axios';
import { createAuthedThunk } from '../authedThunk';

const fetchListInitialState = {
  fetching: null,
  fetchingSuccessful: null,
  fetchingFailed: null,
  error: null,
  errorText: null,
  companies: [],
  // Which creator the cached list was fetched for (null = unscoped). Consumers
  // that need the unscoped list refetch when this is set - see CompanyAutocomplete.
  createdBy: null,
};

const fetchOneInitialState = {
  fetching: null,
  fetchingSuccessful: null,
  fetchingFailed: null,
  error: null,
  errorText: null,
  company: null,
};

const searchInitialState = {
  searching: null,
  searchSuccessful: null,
  searchFailed: null,
  error: null,
  errorText: null,
  results: [],
};

const mutationInitialState = {
  processing: null,
  done: null,
  failed: null,
  error: null,
  errorText: null,
  data: null,
};

const initialState = {
  companies: { ...fetchListInitialState },
  companyById: { ...fetchOneInitialState },
  companySearch: { ...searchInitialState },
  createCompany: { ...mutationInitialState },
  updateCompany: { ...mutationInitialState },
  deleteCompany: { ...mutationInitialState },
  regenerateAboutIndustry: { ...mutationInitialState },
};

// Shared auth wrapper - see `redux/authedThunk`. Was a byte-identical
// local copy in each of eleven slices.
const thunk = createAuthedThunk;

/**
 * Fetches companies, optionally scoped to the ones a given user created.
 *
 * Both endpoints scope non-admin callers to their own records server-side, so
 * the `createdBy` branch exists purely for org admins browsing someone else's
 * companies - see `isAdminUser`. Without `createdBy` we stay on v1, which keeps
 * every existing caller (autocompletes, deal dialogs) on the endpoint it has
 * always used and returns the whole org for admins.
 *
 * @param {Object} [arg]
 * @param {string} [arg.createdBy] - User id to scope to. Org admins only;
 *   ignored (forced to the caller's own id) for everyone else.
 */
export const fetchCompanies = thunk(
  'companies/fetchCompanies',
  async (token, { createdBy } = {}) => {
    const config = {
      params: { 'request-id': uuidv4() },
      headers: { Authorization: `Bearer ${token}` },
    };
    const r = await axios.post(
      '/us/qai/companies/v2/list',
      { createdBy },
      config,
    );
    if (r.status === 200) return r.data;
    throw new Error('Failed to fetch companies');
  },
);

export const fetchCompanyById = thunk(
  'companies/fetchCompanyById',
  async (token, { companyId }) => {
    const r = await axios.get(`/us/qai/companies/${companyId}`, {
      params: { 'request-id': uuidv4() },
      headers: { Authorization: `Bearer ${token}` },
    });
    if (r.status === 200) return r.data;
    throw new Error('Failed to fetch company');
  },
);

/**
 * Searches companies, optionally scoped to one creator - same admin-only
 * `createdBy` rules as `fetchCompanies`.
 *
 * Note that creator scoping on the search path (v1 and v2 alike) relies on the
 * search index carrying a `created_by` field per record, which rolls out
 * separately from the Firestore-backed list endpoints.
 */
export const searchCompaniesThunk = thunk(
  'companies/searchCompanies',
  async (token, { keywords, createdBy } = {}) => {
    const config = {
      params: { 'request-id': uuidv4() },
      headers: { Authorization: `Bearer ${token}` },
    };
    const r = createdBy
      ? await axios.post(
          '/us/qai/companies/v2/search',
          { keywords, createdBy },
          config,
        )
      : await axios.post('/us/qai/companies/search', { keywords }, config);
    if (r.status === 200) return r.data;
    throw new Error('Failed to search companies');
  },
);

export const createCompanyThunk = thunk(
  'companies/createCompany',
  async (token, payload) => {
    const r = await axios.post('/us/qai/companies', payload, {
      params: { 'request-id': uuidv4() },
      headers: { Authorization: `Bearer ${token}` },
    });
    if (r.status === 200) return r.data;
    throw new Error('Failed to create company');
  },
);

export const updateCompanyThunk = thunk(
  'companies/updateCompany',
  async (token, { companyId, payload }) => {
    const r = await axios.put(`/us/qai/companies/${companyId}`, payload, {
      params: { 'request-id': uuidv4() },
      headers: { Authorization: `Bearer ${token}` },
    });
    if (r.status === 200) return r.data;
    throw new Error('Failed to update company');
  },
);

export const deleteCompanyThunk = thunk(
  'companies/deleteCompany',
  async (token, { companyId }) => {
    const r = await axios.delete(`/us/qai/companies/${companyId}`, {
      params: { 'request-id': uuidv4() },
      headers: { Authorization: `Bearer ${token}` },
    });
    if (r.status === 200) return r.data;
    throw new Error('Failed to delete company');
  },
);

export const regenerateAboutIndustryThunk = thunk(
  'companies/regenerateAboutIndustry',
  async (token, { companyId }) => {
    const r = await axios.post(
      `/us/qai/companies/${companyId}/aboutIndustry/regenerate`,
      {},
      {
        params: { 'request-id': uuidv4() },
        headers: { Authorization: `Bearer ${token}` },
      },
    );
    if (r.status === 200) return r.data;
    throw new Error('Failed to regenerate aboutIndustry');
  },
);

const companiesSlice = createSlice({
  name: 'companies',
  initialState,
  reducers: {
    resetCompanySearch: (state) => {
      state.companySearch = { ...searchInitialState };
    },
    clearCompanyById: (state) => {
      state.companyById = { ...fetchOneInitialState };
    },
  },
  extraReducers: (builder) => {
    const addF = (t, k, listKey, trackCreatedBy) =>
      builder
        .addCase(t.pending, (s) => {
          s[k].fetching = true;
          s[k].fetchingSuccessful = false;
          s[k].fetchingFailed = false;
        })
        .addCase(t.fulfilled, (s, a) => {
          s[k].fetching = false;
          s[k].fetchingSuccessful = true;
          s[k].fetchingFailed = false;
          s[k][listKey] = a.payload;
          if (trackCreatedBy) s[k].createdBy = a.meta.arg?.createdBy || null;
        })
        .addCase(t.rejected, (s, a) => {
          s[k].fetching = false;
          s[k].fetchingSuccessful = false;
          s[k].fetchingFailed = true;
          s[k].error = a.payload;
        });

    const addM = (t, k) =>
      builder
        .addCase(t.pending, (s) => {
          s[k].processing = true;
          s[k].done = false;
          s[k].failed = false;
        })
        .addCase(t.fulfilled, (s, a) => {
          s[k].processing = false;
          s[k].done = true;
          s[k].failed = false;
          s[k].data = a.payload;
        })
        .addCase(t.rejected, (s, a) => {
          s[k].processing = false;
          s[k].done = false;
          s[k].failed = true;
          s[k].error = a.payload;
        });

    addF(fetchCompanies, 'companies', 'companies', true);
    addF(fetchCompanyById, 'companyById', 'company');

    builder
      .addCase(searchCompaniesThunk.pending, (s) => {
        s.companySearch.searching = true;
        s.companySearch.searchSuccessful = false;
        s.companySearch.searchFailed = false;
      })
      .addCase(searchCompaniesThunk.fulfilled, (s, a) => {
        s.companySearch.searching = false;
        s.companySearch.searchSuccessful = true;
        s.companySearch.searchFailed = false;
        s.companySearch.results = a.payload;
      })
      .addCase(searchCompaniesThunk.rejected, (s, a) => {
        s.companySearch.searching = false;
        s.companySearch.searchSuccessful = false;
        s.companySearch.searchFailed = true;
        s.companySearch.error = a.payload;
      });

    addM(createCompanyThunk, 'createCompany');
    addM(updateCompanyThunk, 'updateCompany');
    addM(deleteCompanyThunk, 'deleteCompany');
    addM(regenerateAboutIndustryThunk, 'regenerateAboutIndustry');
  },
});

export const { resetCompanySearch, clearCompanyById } = companiesSlice.actions;

export default companiesSlice.reducer;
